import { makeStyles } from '@mui/styles';
import CircularProgress from 'apollo-react/components/CircularProgress';
import IconButton from 'apollo-react/components/IconButton';
import Table, { compareStrings, createStringSearchFilter } from 'apollo-react/components/Table';
import Tooltip from 'apollo-react/components/Tooltip';
import Download from 'apollo-react-icons/Download';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { downloadBulkImportFile } from '../../../actions/BulkImportActions';
import { BULK_IMPORT_APIS, STATUSES } from '../../../constants/MasterSetupConstants';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import {
  createCustomCompareDatesForTable,
  createCustomDateFilter,
} from '../../../utils/formHistoryUtils';
import DateCell from '../../CommonTableCells/DateCell';
import StatusCell from '../../CommonTableCells/StatusCell';
import DatePickerFilter from '../../TableFilters/DatePickerFilter';
import TextFieldFilter from '../../TableFilters/TextInputFilter';

const useStyles = makeStyles({
  historyTable: {
    border: 'solid 1px #e9e9e9',
    '& thead > tr > th:nth-child(1) > svg': {
      display: 'none !important',
    },
    boxShadow: '0 4px 16px 0 rgb(0 0 0 / 4%)',
    '& thead > tr > th:nth-last-child(-n+2) > svg': {
      display: 'none !important',
    },
  },
  loaderContainer: {
    paddingLeft: '5px',
  },
});

export const DownloadImportFileCell = ({
  row: { ImportKey, isWorkflowMasterSetup, OverallStatusCode },
  downloadType,
}) => {
  const [isLoading, setLoading] = useState(false);

  const translateString = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const isDownloadDisabled =
    OverallStatusCode === STATUSES.IN_PROGRESS && downloadType === BULK_IMPORT_APIS.DOWNLOAD_RESULT;

  const downloadClickHandler = async () => {
    setLoading(true);
    await dispatch(
      downloadBulkImportFile({
        downloadType,
        isWorkflowMasterSetup,
        apiProps: { importKey: ImportKey },
      })
    );
    setLoading(false);
  };

  return isLoading ? (
    <CircularProgress className={classes.loaderContainer} size="30px" variant="indeterminate" />
  ) : (
    <Tooltip title={translateString('Download')} placement="bottom">
      <IconButton
        onClick={downloadClickHandler}
        disabled={isDownloadDisabled}
        aria-label={translateString('Download')}
      >
        <Download />
      </IconButton>
    </Tooltip>
  );
};

const columns = [
  {
    header: 'Code',
    accessor: 'TranslatedEntityKey',
  },
  {
    header: 'Started By',
    accessor: 'StartedBy',
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('StartedBy'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Started Date',
    accessor: 'StartedDate',
    customCell: DateCell,
    sortFunction: createCustomCompareDatesForTable,
    filterFunction: createCustomDateFilter('StartedDate'),
    filterComponent: DatePickerFilter,
  },
  {
    header: 'Status',
    accessor: 'OverallStatusCode',
    customCell: StatusCell,
    sortFunction: compareStrings,
    filterFunction: createStringSearchFilter('OverallStatusCode'),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Initial Import File',
    accessor: 'InitialFile',
    customCell: (rowData) =>
      DownloadImportFileCell({ ...rowData, downloadType: BULK_IMPORT_APIS.DOWNLOAD_INITIAL_FILE }),
  },
  {
    header: 'Import Result File',
    accessor: 'ResultFile',
    customCell: (rowData) =>
      DownloadImportFileCell({ ...rowData, downloadType: BULK_IMPORT_APIS.DOWNLOAD_RESULT }),
  },
];

export const HistoryTable = ({ rows, isLoading }) => {
  const classes = useStyles();
  const translateString = useTranslation();
  const rowsPerPageOptions = [5, 10, 15, translateString('All')];

  return (
    <div className={classes.historyTable}>
      <Table
        rowId="ImportKey"
        columns={columns}
        rows={rows}
        hasScroll
        isLoading={isLoading}
        emptyProps={{
          text: translateString('No data to display'),
        }}
        rowsPerPageOptions={rowsPerPageOptions}
        defaultRowsPerPage={10}
        showFilterIcon
        initialSortedColumn="StartedDate"
        initialSortOrder="desc"
        tablePaginationProps={{
          labelDisplayedRows: ({ from, to, count }) =>
            `${from}-${to} ${translateString('of')} ${count}`,
          truncate: true,
          labelRowsPerPage: translateString('Rows'),
          pageText: translateString('Page'),
          ofText: translateString('of'),
        }}
      />
    </div>
  );
};
